import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import InventoryIcon from '@mui/icons-material/Inventory';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444', dangerDark: '#dc2626',
};

const inp  = { border: `1px solid ${C.cardBorder}`, borderRadius: '7px', fontSize: '12px', height: '28px', width: '100%', padding: '0 8px', outline: 'none', backgroundColor: '#f8fafa', boxSizing: 'border-box', fontFamily: 'inherit' };
const card = { backgroundColor: C.card, borderRadius: '10px', boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, marginBottom: '10px', overflow: 'hidden' };
const cardHead = (color = C.accent) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderBottom: `1px solid ${C.cardBorder}`, backgroundColor: `${color}18`, fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.5px' });
const cardBody = { padding: '10px 12px' };
const lbl = { fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '3px' };

const StockJournalDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const { isAuthenticated, companyId, token, logout } = useAuth();
  const branchId = 0;

  const [data, setData]                     = useState({});
  const [invoiceDetails, setInvoiceDetails] = useState([]);

  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchTableData();
  }, []);

  const fetchTableData = async () => {
    try {
      const res = await fetchData('StockJournalApi', `/${id}`, token);
      if (res.responseCode === '1111') {
        setData(res.response_data.StockJournal);
        setInvoiceDetails(res.response_data.StockJournalDetails);
      }
    } catch (err) {
      console.error('Error fetching stock journal:', err);
      if (err.response?.status === 401) { logout(); history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`); }
    }
  };

  return (
    <div style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: '12px', width: '100%', padding: '6px', boxSizing: 'border-box', backgroundColor: C.bg }}>

      {/* ── Header ── */}
      <div style={{ ...card, overflow: 'visible' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <InventoryIcon style={{ fontSize: 16, color: C.accent }} />
            <span style={{ fontWeight: 800, fontSize: '13px', color: C.text, fontFamily: 'Inter, sans-serif' }}>Stock Journal Details</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Link to={`/StockJournalEdit/${id}`}
              style={{ height: '28px', padding: '0 16px', borderRadius: '7px', border: 'none', backgroundColor: C.accent, color: '#fff', fontWeight: 700, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none', fontFamily: 'inherit' }}>
              ✎ Edit
            </Link>
            <Link to="/StockJournalIndex" onClick={() => localStorage.setItem('shouldReload', 'true')}
              style={{ height: '28px', padding: '0 14px', borderRadius: '7px', border: `1px solid ${C.cardBorder}`, backgroundColor: C.card, color: C.muted, fontWeight: 600, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              ← Back
            </Link>
          </div>
        </div>
      </div>

      {/* ── Bill Details ── */}
      <div style={card}>
        <div style={cardHead()}>📋 Bill Details</div>
        <div style={{ ...cardBody, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
          <div>
            <label style={lbl}>Bill Date</label>
            <input type="date" value={formatDateForInput(data.BillDate)} readOnly style={inp} />
          </div>
          <div>
            <label style={lbl}>Bill No</label>
            <input type="text" value={`${data.BillPrefix || ''}${data.BillNumber || ''}/${data.FinYear || ''}`} readOnly style={inp} />
          </div>
          <div>
            <label style={lbl}>Store Location</label>
            <input type="text" value={data.StoreLocationName || ''} readOnly style={{ ...inp, backgroundColor: '#f0f4f4', cursor: 'default' }} />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={lbl}>Remarks</label>
            <input type="text" value={data.Remarks || ''} readOnly style={inp} />
          </div>
        </div>
      </div>

      {/* ── Items table ── */}
      <div style={card}>
        <div style={cardHead('#1F2A38')}>📦 Items ({invoiceDetails.length})</div>
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead style={{ backgroundColor: '#1F2A38', color: '#fff', position: 'sticky', top: 0 }}>
              <tr>
                {['#', 'Item Name', 'Qty'].map((h, i) => (
                  <th key={i} style={{ padding: '6px 8px', textAlign: i >= 2 ? 'right' : 'left', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoiceDetails.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: '20px', color: C.muted, fontSize: '12px' }}>No items found</td></tr>
              ) : invoiceDetails.map((item, index) => (
                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: `1px solid ${C.cardBorder}` }}>
                  <td style={{ padding: '3px 8px', color: C.muted, fontSize: '11px' }}>{index + 1}</td>
                  <td style={{ padding: '3px 8px', fontWeight: 600 }}>{item.ItemName}</td>
                  <td style={{ padding: '3px 8px', textAlign: 'right', fontWeight: 600, color: '#0D47A1' }}>{item.Quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default StockJournalDetails;
